import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../i18n/apiError";
import { apiService } from "../services/api";
import { ProjectAccessRequest, Project, ApproveAccessRequestRequest } from "../types/api";

export const AccessRequestManager: React.FC = () => {
  const { t } = useTranslation("accessRequestManager");
  const [requests, setRequests] = useState<ProjectAccessRequest[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);

  // Approval form state
  const [selectedRequest, setSelectedRequest] = useState<ProjectAccessRequest | null>(null);
  const [approvalForm, setApprovalForm] = useState<ApproveAccessRequestRequest>({
    project_id: 0,
    role: "researcher",
    notes: "",
  });
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);

  // Rejection form state
  const [rejectingRequest, setRejectingRequest] = useState<ProjectAccessRequest | null>(null);
  const [rejectionNotes, setRejectionNotes] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load pending access requests and user's projects in parallel
      const [requestsResponse, profileResponse] = await Promise.all([
        apiService.getPendingAccessRequests(),
        apiService.getProfile(),
      ]);

      setRequests(requestsResponse.requests || []);
      setProjects(profileResponse.projects || []);
    } catch (err) {
      setError(translateApiError(t, err));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (request: ProjectAccessRequest) => {
    setSelectedRequest(request);
    setApprovalForm({
      project_id: 0,
      role: "researcher",
      notes: "",
    });
    setShowApprovalDialog(true);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    if (approvalForm.project_id === 0) {
      alert(t("errors.selectProjectRequired"));
      return;
    }

    try {
      setProcessingId(selectedRequest.id);
      await apiService.approveAccessRequest(selectedRequest.id, approvalForm);

      // Reload requests
      await loadData();
      setShowApprovalDialog(false);
      setSelectedRequest(null);
    } catch (err) {
      alert(translateApiError(t, err));
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectClick = (request: ProjectAccessRequest) => {
    setRejectingRequest(request);
    setRejectionNotes("");
    setShowRejectDialog(true);
  };

  const handleReject = async () => {
    if (!rejectingRequest) return;

    try {
      setProcessingId(rejectingRequest.id);
      await apiService.rejectAccessRequest(rejectingRequest.id, rejectionNotes || undefined);

      // Reload requests
      await loadData();
      setShowRejectDialog(false);
      setRejectingRequest(null);
    } catch (err) {
      alert(translateApiError(t, err));
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending_email_verification":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
            {t("statusBadge.awaitingEmailVerification")}
          </span>
        );
      case "pending":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
            {t("statusBadge.pendingReview")}
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">{t("page.title")}</h1>
        <p className="text-gray-600 mt-2">{t("page.subtitle")}</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">{t("empty.noRequests")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {request.user_name}
                    </h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{t("card.email")}</span> {request.user_email}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{t("card.institution")}</span>{" "}
                    {request.user_institution}
                  </p>
                  {request.project_title_description && (
                    <p className="text-sm text-gray-600 mt-2">
                      <span className="font-medium">{t("card.projectInterest")}</span>{" "}
                      {request.project_title_description}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    {t("card.requested", {
                      date: new Date(request.created_at).toLocaleDateString(),
                    })}
                  </p>
                  {request.is_new_user && (
                    <div className="mt-2">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-accent-pale text-accent">
                        {t("card.newUser")}
                      </span>
                    </div>
                  )}
                </div>

                {request.status === "pending" && (
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleApproveClick(request)}
                      disabled={processingId === request.id}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {processingId === request.id ? t("actions.processing") : t("actions.approve")}
                    </button>
                    <button
                      onClick={() => handleRejectClick(request)}
                      disabled={processingId === request.id}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {t("actions.reject")}
                    </button>
                  </div>
                )}

                {request.status === "pending_email_verification" && (
                  <div className="ml-4">
                    <span className="text-sm text-gray-500 italic">
                      {t("card.mustVerifyEmail")}
                    </span>
                  </div>
                )}
              </div>

              {request.previous_rejections && request.previous_rejections.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-orange-700 mb-2">
                    {t("card.previousRejections")}
                  </p>
                  <div className="space-y-2">
                    {request.previous_rejections.map((rejection, idx) => (
                      <div key={idx} className="text-xs text-gray-600 bg-orange-50 p-2 rounded">
                        <span className="font-medium">
                          {new Date(rejection.rejected_at).toLocaleDateString()}
                        </span>
                        {rejection.reason && ` - ${rejection.reason}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Approval Dialog */}
      {showApprovalDialog && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {t("approveDialog.title")}
            </h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">{t("approveDialog.user")}</span> {selectedRequest.user_name}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">{t("approveDialog.email")}</span> {selectedRequest.user_email}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("approveDialog.assignToProject")}
                </label>
                <select
                  value={approvalForm.project_id}
                  onChange={(e) =>
                    setApprovalForm((prev) => ({
                      ...prev,
                      project_id: parseInt(e.target.value),
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value={0}>{t("approveDialog.selectProjectPlaceholder")}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("approveDialog.role")}
                </label>
                <select
                  value={approvalForm.role}
                  onChange={(e) =>
                    setApprovalForm((prev) => ({
                      ...prev,
                      role: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="researcher">{t("approveDialog.roleResearcher")}</option>
                  <option value="pi">{t("approveDialog.rolePi")}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("approveDialog.notes")}
                </label>
                <textarea
                  value={approvalForm.notes}
                  onChange={(e) =>
                    setApprovalForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={t("approveDialog.notesPlaceholder")}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleApprove}
                disabled={processingId !== null}
                className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {processingId ? t("approveDialog.approving") : t("approveDialog.approve")}
              </button>
              <button
                onClick={() => {
                  setShowApprovalDialog(false);
                  setSelectedRequest(null);
                }}
                disabled={processingId !== null}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("approveDialog.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Dialog */}
      {showRejectDialog && rejectingRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {t("rejectDialog.title")}
            </h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">{t("rejectDialog.user")}</span> {rejectingRequest.user_name}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">{t("rejectDialog.email")}</span> {rejectingRequest.user_email}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("rejectDialog.reason")}
              </label>
              <textarea
                value={rejectionNotes}
                onChange={(e) => setRejectionNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t("rejectDialog.reasonPlaceholder")}
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleReject}
                disabled={processingId !== null}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {processingId ? t("rejectDialog.rejecting") : t("rejectDialog.reject")}
              </button>
              <button
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectingRequest(null);
                }}
                disabled={processingId !== null}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("rejectDialog.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
